import React, { useContext, useEffect, useState } from 'react'

import { Button } from '@/components/atoms'
import { Checkbox } from '@/components/atoms/checkbox'
import { modalContainerStyle } from '@/components/pages/accommodation-management/list-layout'
import { DateRangeField } from '@/components/molecules/date-range-field'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import {
  fetchReservationConfirmedSales,
  fetchReservationLink,
  insertAdminReservationCheckin,
  updateSalesAdvancePayment,
} from '@/apis/aipass'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { AccountContext } from '@/contexts/account'
import { errorHandler } from '@/libs/errors'
import type { ReservationType } from '@/models/reservation'

const DISPLAY_LIMIT_IN_PAGE = 30

interface SelectRelevantModalProps {
  reservation: ReservationType | undefined
  onClose: () => void
}

export const SelectRelevantModal: React.FC<SelectRelevantModalProps> = ({ reservation, onClose }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [lastPage, setLastPage] = useState<number>(1)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalNumber, setTotalNumber] = useState<number>(0)
  const [reservationList, setReservationList] = useState<any>([])
  const [selectedReservationIds, setSelectedReservationIds] = useState<string[]>([])
  const [beginDate, setBeginDate] = useState<string>(dayjs(reservation?.checkinDate).format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState<string>(dayjs(reservation?.checkinDate).add(1, 'day').format('YYYY-MM-DD'))

  // "1" part of 1-30 (30*(1-1)+1 = 1st page)
  const displayFrom = totalNumber === 0 ? 0 : DISPLAY_LIMIT_IN_PAGE * (currentPage - 1) + 1
  // "1" part of 1-30 (30*(1-1)+1 = 1st page)
  const displayTo = Math.min(DISPLAY_LIMIT_IN_PAGE * currentPage, totalNumber)
  const params = useParams<any>()
  const { id } = params

  const { t } = useTranslation()
  const { account } = useContext<any>(AccountContext)

  // go back
  const onClickPrevious = () => {
    if (currentPage === 1) return
    setCurrentPage(currentPage - 1)
  }

  // move on
  const onClickNext = () => {
    if (currentPage === lastPage) return
    setCurrentPage(currentPage + 1)
  }

  // Check if there are confirmed reservation details
  const _hasConfirmedSales = async (reservationIds: string[]): Promise<boolean> => {
    for (const reservationId of reservationIds) {
      const response = await fetchReservationConfirmedSales(reservationId).catch(() => null)
      if ((response?.sales?.length ?? 0) > 0) {
        return true
      }
    }

    return false
  }

  // Reservation and linking from check-in
  const _setReservationCheckin = async () => {
    if (selectedReservationIds.length === 0) return
    setIsLoading(true)
    // If any reservation has confirmed details, confirm whether to link them
    if (
      (await _hasConfirmedSales(selectedReservationIds)) &&
      !window.confirm(t('Includes items with confirmed dates Please pay attention to sales management'))
    ) {
      setIsLoading(false)
      return
    }

    try {
      // Give Advance Receipt Flag before linking
      await updateSalesAdvancePayment(selectedReservationIds)
      // Match the time to overwrite and update the check-in time and check-out time of the corresponding check-in information in the reservation information
      await insertAdminReservationCheckin({ checkinId: id, reservationIds: selectedReservationIds })
      window.location.reload()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Reservation information acquisition by paging
  const _fetchReservationInfoByPaging = async () => {
    setIsLoading(true)
    try {
      const res = await fetchReservationLink({
        beginDate,
        endDate,
        page: currentPage,
      })

      if (!res) {
        setReservationList([])
        setTotalNumber(0)
        setLastPage(1)
        return
      }

      setReservationList(res.reservations || [])
      setTotalNumber(res.total || 0)
      setLastPage(Math.max(1, Math.ceil((res.total || 0) / DISPLAY_LIMIT_IN_PAGE)))
    } finally {
      setIsLoading(false)
    }
  }

  const _changeRangeDate = (beginDate, endDate) => {
    setCurrentPage(1)
    setSelectedReservationIds([])
    setBeginDate(beginDate)
    setEndDate(endDate)
  }

  useEffect(() => {
    if (account && account?.hotel) {
      if (beginDate && endDate) {
        _fetchReservationInfoByPaging()
      }
    }
  }, [account, currentPage, beginDate, endDate])

  return (
    <>
      <div css={[modalContainerStyle, { zIndex: 103 }]}></div>
      <div css={modalBodyContainerStyle}>
        {isLoading && <div css={loadingOverlayStyle} />}
        <h3 css={modalHeaderStyle}>{t('Select relevant booking')}</h3>
        <div css={modalMainContainerStyle}>
          <div css={dateRangeFieldContainerStyle}>
            <DateRangeField beginDate={beginDate} endDate={endDate} changeRangeDate={_changeRangeDate} />
          </div>
          <ul css={reservationLabelContainerStyle}>
            <li css={reservationUserLabelStyle}>{t('Reservation name')}</li>
            <li css={reservationGuestLabelStyle}>{t('Guests')}</li>
            <li css={reservationCheckinLabelStyle}>{t('Date of stay')}</li>
          </ul>
          <div css={modalMainCheckInListContainerStyle}>
            {reservationList &&
              reservationList?.length !== 0 &&
              reservationList?.map(v => (
                <li
                  key={v.reservationId}
                  css={selectedReservationIds.includes(v?.reservationId) ? reservationContentsActiveStyle : reservationContentsStyle}
                  onClick={() => {
                    setSelectedReservationIds(prev =>
                      prev.includes(v.reservationId) ? prev.filter(id => id !== v.reservationId) : [...prev, v.reservationId],
                    )
                  }}
                >
                  <Checkbox value={selectedReservationIds.includes(v.reservationId)} style={reservationCheckBoxStyle} />
                  <ul css={reservationUserWrapperStyle}>
                    <li css={reservationGuestNameKanaStyle}>
                      {v?.reservationUserNameKana === null || v?.reservationUserNameKana === '' ? '-' : v.reservationUserNameKana}
                    </li>
                    <li css={reservationGuestNameStyle}>
                      {v?.reservationUserName === null || v?.reservationUserName === '' ? '-' : v.reservationUserName}
                    </li>
                  </ul>
                  <ul css={reservationGuestWrapperStyle}>
                    <li css={reservationGuestNameKanaStyle}>
                      {v?.reservationGuestNameKana === null || v?.reservationGuestNameKana === '' ? '-' : v.reservationGuestNameKana}
                    </li>
                    <li css={reservationGuestNameStyle}>
                      {v?.reservationGuestName === null || v?.reservationGuestName === '' ? '-' : v.reservationGuestName}
                    </li>
                  </ul>
                  <div css={reservationScheduleDayStyle}>
                    {v?.reservationCheckinDate ? dayjs(v?.reservationCheckinDate).format('MM月DD日(ddd)') : '-'}
                  </div>
                </li>
              ))}
          </div>
          <div css={detailListPaginationContainerStyle}>
            <p css={paginationLineCountStyle}>
              {displayFrom}-{displayTo} / {totalNumber} {t('Rows')}
            </p>
            <div css={paginationArrowStyle} onClick={onClickPrevious}>
              {displayFrom === 1 ? (
                <img alt="arrow_disabled_left" src={require('@/static/images/arrow_disabled_left.svg')} onClick={onClickPrevious} />
              ) : (
                <img alt="arrow_left" src={require('@/static/images/arrow_left.svg')} onClick={onClickPrevious} />
              )}
            </div>
            <div css={paginationArrowStyle} onClick={onClickNext}>
              {displayTo === totalNumber ? (
                <img alt="arrow_disabled_right" src={require('@/static/images/arrow_disabled_right.svg')} onClick={onClickNext} />
              ) : (
                <img alt="arrow_right" src={require('@/static/images/arrow_right.svg')} onClick={onClickNext} />
              )}
            </div>
          </div>
        </div>
        <div css={modalButtonSectionStyle}>
          <Button width={110} height={38} buttonType={3} marginRight={16} onClick={() => onClose()}>
            {t('Cancel')}
          </Button>
          <Button
            width={110}
            height={40}
            buttonType={selectedReservationIds.length > 0 ? 1 : 5}
            onClick={() => {
              selectedReservationIds.length > 0 && _setReservationCheckin()
            }}
          >
            {t('Completion')}
          </Button>
        </div>
      </div>
    </>
  )
}

// Modal
const modalBodyContainerStyle = css({
  position: 'fixed',
  top: '50%',
  left: '50%',
  minWidth: 680,
  zIndex: 103,
  opacity: 1,
  background: '#FFF',
  borderRadius: 5,
  transform: 'translate(-50%, -50%)',
})

// 処理中オーバーレイ
const loadingOverlayStyle = css({
  position: 'absolute',
  zIndex: 105,
  width: '100%',
  height: '100%',
  background: 'rgba(255, 255, 255, 0.8)',
  opacity: 0.7,
  alignItems: 'center',
  borderRadius: 5,
})

const modalHeaderStyle = css({
  margin: '22px 32px',
  fontWeight: 'bold',
  fontSize: 18,
  color: '#676767',
})

const modalMainContainerStyle = css({
  padding: '26px 32px',
  backgroundColor: '#F2F2F2',
})

const dateRangeFieldContainerStyle = css({
  marginBottom: 24,
})

const reservationUserLabelStyle = css({
  marginLeft: 38,
  width: 140,
})

const reservationGuestLabelStyle = css({
  marginLeft: 20,
  width: 140,
})

const reservationCheckinLabelStyle = css({
  marginLeft: 'auto',
  minWidth: 92,
})

const modalMainCheckInListContainerStyle = css({
  width: '100%',
  height: 256,
  backgroundColor: '#FFF',
  border: '1px solid #CCCCCC',
  borderTop: 'none',
  borderRadius: '0 0 5px 5px',
  overflow: 'auto',
})

const reservationLabelContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: '#FFF',
  padding: '10px 36px',
  color: '#676767',
  fontWeight: 'bold',
  fontSize: 12,
  borderRadius: '5px 5px 0 0',
  borderLeft: '1px solid #CCCCCC',
  borderTop: '1px solid #CCCCCC',
  borderRight: '1px solid #CCCCCC',
  borderBottom: '1px solid #ccc',
})

const reservationContentsStyle = css({
  cursor: 'pointer',
  minHeight: 64,
  display: 'flex',
  alignItems: 'center',
  padding: '12px 27px 12px 32px',
  borderBottom: '1px solid #ccc',
  ':hover': {
    boxShadow: '0px 3px 6px #0000000f',
  },
})

const reservationContentsActiveStyle = css(reservationContentsStyle, {
  backgroundColor: '#fef3f0',
})

const reservationUserWrapperStyle = css({
  color: '#676767',
  fontSize: 14,
  width: 140,
  justifyContent: 'space-between',
  fontWeight: 'bold',
  display: 'flex',
  marginLeft: 25,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  flexDirection: 'column',
})

const reservationGuestWrapperStyle = css({
  color: '#676767',
  fontSize: 14,
  width: 140,
  justifyContent: 'space-between',
  fontWeight: 'bold',
  display: 'flex',
  marginLeft: 20,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  flexDirection: 'column',
})

const reservationScheduleDayStyle = css({
  color: '#676767',
  fontSize: 14,
  marginLeft: 'auto',
  minWidth: 92,
  textAlign: 'center',
})

const modalButtonSectionStyle = css({
  display: 'flex',
  padding: '11px 32px 11px 32px',
  justifyContent: 'flex-end',
  boxShadow: '0px 0px 6px #0000001A',
})

const reservationGuestNameKanaStyle = css({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: 12,
  paddingBottom: 6,
})

const reservationGuestNameStyle = css({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: 14,
})

const reservationCheckBoxStyle = css({
  img: { marginRight: 0 },
})

const detailListPaginationContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: 30,
  width: '100%',
  justifyContent: 'flex-end',
  marginTop: 15,
})

const paginationLineCountStyle = css({
  fontSize: 12,
  color: '#676767',
  marginRight: 16,
  padding: '8px 0',
  fontWeight: 'bold',
})

const paginationArrowStyle = css({
  borderRadius: '50%',
  height: 30,
  ':hover': {
    backgroundColor: '#F2F2F2',
    cursor: 'pointer',
  },
})
